<template>
  <layout-basic>
    <v-container fluid class="pa-4 pa-md-6">
      <div v-if="phase === 'loading'" class="welcome-loading">
        <v-progress-circular indeterminate color="primary" />
        <p>Loading your first steps…</p>
      </div>
      <live-webinar-invite
        v-else-if="phase === 'live'"
        @continue-setup="onLiveContinue"
        @back-to-setup="onBackToSetup"
      />
      <!-- The wizard IS the welcome page (18 Sep 2026, Muhammad: the launcher
           card that used to come first "feels like an additional step").
           The two-minute tour starts from the wizard's own link, and the
           Setup guide button is always there in the corner. -->
      <onboarding-wizard
        v-else
        @start-tour="onStartTour"
        @join-live="onJoinLive"
      />
    </v-container>
  </layout-basic>
</template>

<script>
import LayoutBasic from "@/layouts/Dashboard";
import OnboardingWizard from "@/components/authenticated/welcome/OnboardingWizard";
import LiveWebinarInvite from "@/components/authenticated/welcome/LiveWebinarInvite";
import companyAPI from "@/api/company";
import tour from "@/utils/tour";
import { isTrialCompanyAdmin, mergeOnboardingPayload } from "@/utils/onboarding";

export default {
  name: "Welcome",
  components: {
    LayoutBasic,
    OnboardingWizard,
    LiveWebinarInvite
  },
  data() {
    return {
      phase: "loading"
    };
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      if (!this.$store.getters.companyState) {
        try {
          await this.$store.dispatch("profile");
        } catch (e) {
          // Continue with whatever is already in the store
        }
      }
      // TRIAL ADMINS ONLY - the whole point of this page. Paid admins were
      // once redirected in on a fresh browser, and any learner could open
      // /welcome by URL and even flip the company's onboarding state via
      // Skip. Gate the PAGE, using the same predicate the tour uses, so every
      // entry path and every build is covered.
      if (!isTrialCompanyAdmin(this.$store.getters)) {
        this.$router.replace(
          this.$store.getters.admin ? "/admin/users" : "/"
        );
        return;
      }
      // Warm the local cache with the server's progress so the tour
      // controller resumes from the truth (the wizard loads its own), then
      // show the wizard.
      try {
        const resp = await companyAPI.getOnboardingProgress();
        const data = resp.data && resp.data.data ? resp.data.data : {};
        mergeOnboardingPayload(data);
      } catch (e) {
        // Local cache is enough if the API is unavailable
      }
      this.phase = "wizard";
    },
    async persistFlags(updates) {
      const payload = mergeOnboardingPayload(updates);
      try {
        await companyAPI.updateOnboardingProgress(payload);
      } catch (e) {
        // Keep local cache even if server save fails
      }
    },
    onStartTour() {
      // The controller takes over: it navigates the real admin screens,
      // resumes from a saved step if there is one, and persists the outcome
      // (completed -> tourCompleted, skipped -> tourSkipped) itself. By the
      // time the tour ends the user is standing in the app, with the Setup
      // guide widget for anything more, and "/" brings them back here until
      // the wizard is finished. "tour" is the guided tour itself (three
      // stops), not a Setup guide chapter.
      tour.startChapter("tour", {
        router: this.$router,
        storeGetters: this.$store.getters
      });
    },
    async onJoinLive() {
      await this.persistFlags({ webinarOffered: true });
      this.phase = "live";
    },
    async onLiveContinue() {
      await this.persistFlags({ webinarDismissed: true });
      this.phase = "wizard";
    },
    onBackToSetup() {
      this.phase = "wizard";
    }
  }
};
</script>

<style scoped>
.welcome-loading {
  max-width: 1180px;
  margin: 48px auto;
  text-align: center;
  color: #5a6a7a;
}

.welcome-loading p {
  margin-top: 16px;
}
</style>
